/**
 * SocialStatsCard — Konto-Statistiken (Follower, Likes, Videos) pro verbundener
 * Plattform (TikTok via user.info.stats, Instagram/Facebook via Graph API).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Users, Heart, Clapperboard, Music2, Instagram, Facebook, TrendingUp, TrendingDown } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = {
  tiktok:    { label: "TikTok",    icon: Music2,    color: "#0F0F0F" },
  instagram: { label: "Instagram", icon: Instagram, color: "#E1306C" },
  facebook:  { label: "Facebook",  icon: Facebook,  color: "#1877F2" },
};

const fmt = (n) => (n === undefined || n === null ? "—" : new Intl.NumberFormat("de-CH").format(n));

export default function SocialStatsCard({ companyId }) {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const params = companyId && companyId !== "all" ? { company_id: companyId } : {};
      const [r, h] = await Promise.all([
        axios.get(`${API}/stats/social`, { params, withCredentials: true }),
        axios.get(`${API}/stats/social/history`, { params: { ...params, days: 90 }, withCredentials: true }),
      ]);
      setItems(r.data.items || []);
      setLastSync(r.data.last_sync || null);
      setHistory(h.data.items || []);
    } catch { /* leer lassen */ }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSync = async () => {
    setSyncing(true);
    try { await axios.post(`${API}/stats/sync`, {}, { withCredentials: true }); await load(); }
    catch { /* noop */ }
    finally { setSyncing(false); }
  };

  // Chart-Daten: pro Datum eine Zeile, Follower je Plattform (ueber Firmen summiert)
  const chartPlatforms = [...new Set(history.map((h) => h.platform))];
  const byDate = {};
  history.forEach((h) => {
    byDate[h.date] = byDate[h.date] || { date: h.date };
    byDate[h.date][h.platform] = (byDate[h.date][h.platform] || 0) + (h.followers || 0);
  });
  const chartData = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));

  // 7-Tage-Delta je Konto (Firma + Plattform)
  const deltaFor = (s) => {
    const rows = history.filter((h) => h.platform === s.platform && h.company_id === s.company_id);
    if (rows.length < 2) return null;
    const cutoff = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
    const base = rows.filter((r) => r.date <= cutoff).pop() || rows[0];
    if (!base || base.followers === undefined) return null;
    return (s.followers ?? rows[rows.length - 1].followers) - base.followers;
  };

  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid="social-stats-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="font-heading text-lg">Social-Konto-Statistiken</CardTitle>
          {lastSync && (
            <p className="text-[11px] text-[var(--studio-text-muted)] mt-0.5">
              Letzter Sync: {new Date(lastSync).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleSync} disabled={syncing} data-testid="social-stats-sync-btn">
          {syncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          Aktualisieren
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-[var(--studio-text-muted)]" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-[var(--studio-text-muted)]" data-testid="social-stats-empty">
            Noch keine Konto-Statistiken — klicke auf „Aktualisieren", um die Daten der verbundenen Konten (TikTok, Instagram, Facebook) abzurufen.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((s) => {
              const p = PLATFORMS[s.platform] || { label: s.platform, icon: Users, color: "#64748B" };
              const Icon = p.icon;
              const delta = deltaFor(s);
              return (
                <div key={`${s.company_id}-${s.platform}`} className="border border-[var(--studio-border)] rounded-xl p-4" data-testid={`social-stats-${s.platform}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${p.color}15` }}>
                      <Icon className="w-4 h-4" style={{ color: p.color }} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-tight">{p.label}</p>
                      <p className="text-[11px] text-[var(--studio-text-muted)] truncate">{s.account_name ? `@${s.account_name}` : ""}</p>
                    </div>
                    {delta !== null && delta !== 0 && (
                      <span className={`text-[11px] font-semibold flex items-center gap-0.5 ${delta > 0 ? "text-emerald-600" : "text-red-600"}`}
                        data-testid={`social-stats-delta-${s.platform}`}>
                        {delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {delta > 0 ? "+" : ""}{fmt(delta)} / 7T
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold font-heading leading-none">{fmt(s.followers)}</p>
                      <p className="text-[10px] text-[var(--studio-text-muted)] mt-1 flex items-center justify-center gap-0.5"><Users className="w-3 h-3" /> Follower</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold font-heading leading-none">{fmt(s.total_likes)}</p>
                      <p className="text-[10px] text-[var(--studio-text-muted)] mt-1 flex items-center justify-center gap-0.5"><Heart className="w-3 h-3" /> Likes</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold font-heading leading-none">{fmt(s.video_count)}</p>
                      <p className="text-[10px] text-[var(--studio-text-muted)] mt-1 flex items-center justify-center gap-0.5"><Clapperboard className="w-3 h-3" /> Beiträge</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && chartData.length >= 2 && (
          <div className="mt-5" data-testid="social-growth-chart">
            <p className="text-xs font-medium text-[var(--studio-text-muted)] mb-2 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> Follower-Wachstum (90 Tage)
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--studio-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }}
                  tickFormatter={(d) => new Date(d).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit" })} />
                <YAxis tick={{ fontSize: 10 }} width={45} allowDecimals={false} domain={["auto", "auto"]} />
                <Tooltip
                  labelFormatter={(d) => new Date(d).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" })}
                  formatter={(v, name) => [fmt(v), PLATFORMS[name]?.label || name]} />
                <Legend formatter={(name) => PLATFORMS[name]?.label || name} wrapperStyle={{ fontSize: 11 }} />
                {chartPlatforms.map((pl) => (
                  <Line key={pl} type="monotone" dataKey={pl} stroke={PLATFORMS[pl]?.color || "#64748B"}
                    strokeWidth={2} dot={{ r: 2 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {!loading && items.length > 0 && chartData.length < 2 && (
          <p className="mt-4 text-[11px] text-[var(--studio-text-muted)]" data-testid="social-growth-hint">
            Die Wachstumskurve erscheint, sobald Daten von mindestens 2 Tagen vorliegen (täglicher automatischer Sync).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
